import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FontAwesome from 'react-fontawesome';
import { Form, Formik } from 'formik';
import camelcaseKeys from 'camelcase-keys';

import FilterSet from 'components/Filters/FilterSet';

import './styles/Filters.scss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const toDisplayNames = (fields, selected) => {
  const displayNames = {};

  if (fields.length) {
    Object.keys(selected).forEach((selectedName) => {
      // Grab the field
      const field = fields.find((f) => f.solr_prompt === selectedName);

      if (!field) {
        return;
      }

      if (field.filter_type === 'TextFilter' || field.filter_type === 'NumberFilter') {
        displayNames[field.display_name] = [selected[selectedName]];
      } else {
        /*
        *   Map the field name and field option values to their display names
        *
        *   {'enroll_grade_id': ['0660af35-4b8d-45e4-a827-641cd46223f', '...']} => {'Enroll Grade': ['1st', '...']}
        * */
        displayNames[field.display_name] = selected[selectedName].map((s) => {
          const option = field.options.find((opt) => {
            if (typeof opt === 'string') {
              return s === opt;
            }
            // eslint-disable-next-line no-unused-vars
            const [_, v] = opt;
            return s === v;
          });

          return Array.isArray(option) ? option[0] : option;
        });
      }
    });
  }
  return displayNames;
};

const Filters = props => {
  const {
    count,
    sublabel,
    itemsName,
    fields,
    loading,
    selected,
    defaultSelected,
    selectCount,
    tooltip,
    submitFilters,
    validate,
  } = props;

  const mappedFields = fields.map((field) => {
    return {
      ...field,
      filter_type: field.ctype === 'TextField' ? 'TextFilter' : field.filter_type
    }
  });

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const handleFilterToggle = () => setOpen(!open);
  const applyFilters = (filters) => {
    const newErrors = {};
    fields.forEach(field => validate({ field, errors: newErrors, values: filters[field.solr_prompt] }));
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setOpen(false);
      submitFilters(filters);
    }
  };
  const selectedTooltip = tooltip && <Tooltip>{tooltip}</Tooltip>;
  const displayNames = toDisplayNames(mappedFields, selected);

  const initialValues = {};
  mappedFields.forEach(({ solr_prompt, filter_type }) => {
    const defaultValue = filter_type ? '' : [];
    initialValues[solr_prompt] = selected[solr_prompt] || defaultValue;
  });

  const removeEmptyValues = (values) => {
    const nonEmpty = {};
    Object.keys(values).forEach(k => {
      if (Array.isArray(values[k])) {
        if (values[k].length > 0) {
          nonEmpty[k] = values[k];
        }
      } else if (values[k] || values[k] === 0 || values[k] === false) {
        nonEmpty[k] = values[k];
      }
    });
    return nonEmpty;
  };

  const clearFiltersText = Object.keys(defaultSelected).length !== 0 ? 'Reset filters' : 'Clear filters';

  return (
    <div className='contact-filters-react'>
      <div className='contact-filters-react-header'>
        <button
          type={'button'}
          className='contact-filters-react-options btn btn-xs btn-primary'
          onClick={handleFilterToggle}
        >
          Filter options
          <FontAwesome name={open ? 'chevron-down' : 'chevron-right'} />
        </button>

        {
          Object.keys(displayNames).map((name, i) => (
            <span className='contact-filters-react-term badge bg-secondary' key={i}>
              {
                displayNames[name].length !== 0 && (
                  <span>
                    <span className='contact-filters-react-term-field'>{name}:</span>
                    <span className='contact-filters-react-term-values'>
                      {displayNames[name].join(', ')}
                    </span>
                  </span>
                )
              }
            </span>
          ))
        }

        {
          Object.keys(selected).length > 0 && (
            <button
              type={'button'}
              className='contact-filters-react-clear-filters btn btn-link'
              onClick={() => applyFilters(defaultSelected)}
              disabled={loading}
            >
              {clearFiltersText}
            </button>
          )
        }

        {selectCount != null && (
          <span className='contact-filters-react-count'>
            | <span className='contact-filters-react-count'>
                <strong>{selectCount}</strong> of {count} {itemsName} selected
            </span>
          </span>
        )}

        {selectCount == null && count != null && <span className='contact-filters-react-count'>
            | {count} {itemsName}
          </span>}

        {sublabel != null && <span className='contact-filters-react-sublabel'>
            | {sublabel}
          </span>}

        {selectedTooltip
          && <OverlayTrigger overlay={selectedTooltip}><FontAwesome name={'info-circle'} /></OverlayTrigger>
        }
      </div>

      {open && (
        <div className='contact-filters-react-body card card-body bg-light'>
          <Formik
            initialValues={initialValues}
            onSubmit={(values) => applyFilters(removeEmptyValues(values))}
          >
            <Form>
              {mappedFields.map((field) => <FilterSet key={field.solr_prompt} {...camelcaseKeys(field)} />)}

              <div className='contact-filter-react-buttons'>
                <button className='btn btn-primary' type='submit' disabled={loading}>
                  Apply Filters
                </button>

                <button type={'button'}
                        className='btn btn-danger-inline'
                        onClick={() => applyFilters(defaultSelected)}
                        disabled={loading}>
                  {clearFiltersText}
                </button>

                {Object.keys(errors).length !== 0 && <div className='contact-filter-react-errors'>
                  {fields
                    .filter(field => errors[field.solr_prompt])
                    .map((field, i) => `${field.display_name} ${errors[field.solr_prompt]}`)
                    .join(', ')}
                </div>}
              </div>
            </Form>
          </Formik>


        </div>
      )}
    </div>
  );
};

Filters.propTypes = {
  /** The total number of items across all pages */
  count: PropTypes.number,

  /** label to be shown after count */
  sublabel: PropTypes.node,

  /** Name of entities being filtered */
  itemsName: PropTypes.string,

  /** Array of field objects for which filters are available */
  fields: PropTypes.arrayOf(PropTypes.object),

  /** Disables submit button if true */
  loading: PropTypes.bool,

  /** number of items selected */
  selectCount: PropTypes.number,

  /** Current selected filters */
  selected: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.string)
  }),

  /** Default selection. Clearing filters reverts to these selections. */
  defaultSelected: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.string)
  }),

  /** Optional tooltip to display after selection count */
  tooltip: PropTypes.string,

  submitFilters: PropTypes.func,

  /** Optional validation function */
  validate: PropTypes.func,
};

Filters.defaultProps = {
  count: null,
  sublabel: null,
  fields: [],
  loading: false,
  itemsName: 'items',
  selectCount: null,
  selected: {},
  defaultSelected: {},
  tooltip: null,
  submitFilters: () => false,
  validate: () => true,
};

export default Filters;
